/**
 * Pre-mount public UI flag checks.
 *
 * The IIFE mount() decision happens before React or any session/backend
 * connection exists. This module resolves the
 * `super-phoebe.launcher.enabled` GrowthBook flag via a small backend HTTP
 * endpoint (not a browser-side GrowthBook client) before mount() decides
 * whether to render at all.
 *
 * Fail-closed contract: any fetch failure, timeout, non-200 response,
 * malformed JSON, or non-boolean `enabled` field resolves to `false`. This
 * function never throws — mount()'s explicit override branch always takes
 * priority and only calls this when window.__PHOEBE_CONFIG__.enabled is
 * undefined (see iife.ts).
 */

export const LAUNCHER_FLAG_PATH = "/api/launcher-flag";
export const SIDE_PANEL_FLAG_PATH = "/api/side-panel-flag";
export const LIVE_CHAT_FLAG_PATH = "/api/live-chat-flag";
export const LAUNCHER_FLAG_TIMEOUT_MS = 500;

/** Converts a Socket.IO backend URL (wss/https) into an HTTP(S) origin usable for a plain fetch. */
function toHttpOrigin(backendUrl: string): string | undefined {
  try {
    const url = new URL(backendUrl);
    url.protocol = url.protocol === "wss:" ? "https:" : url.protocol === "ws:" ? "http:" : url.protocol;
    return url.origin;
  } catch {
    return undefined;
  }
}

/**
 * Resolves whether the launcher should mount, given the already-resolved
 * backend URL. Fails closed (`false`) on any error, timeout, or malformed
 * response — never throws.
 */
async function resolveEnabledFlag(
  backendUrl: string | null | undefined,
  {
    path = LAUNCHER_FLAG_PATH,
    timeoutMs = LAUNCHER_FLAG_TIMEOUT_MS,
    debug = false,
  }: { path?: string; timeoutMs?: number; debug?: boolean } = {},
): Promise<boolean> {
  const origin = backendUrl ? toHttpOrigin(backendUrl) : undefined;
  if (!origin) {
    if (debug) console.log(`[phoebe-chat] ${path}: no backend URL resolved — failing closed`);
    return false;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${origin}${path}`, {
      method: "GET",
      signal: controller.signal,
      credentials: "omit",
    });
    if (response.status !== 200) {
      if (debug) console.log(`[phoebe-chat] ${path}: non-200 response (${response.status}) — failing closed`);
      return false;
    }
    const payload: unknown = await response.json();
    const enabled =
      typeof payload === "object" &&
      payload !== null &&
      Object.prototype.hasOwnProperty.call(payload, "enabled")
        ? (payload as { enabled: unknown }).enabled
        : undefined;
    if (typeof enabled !== "boolean") {
      if (debug) console.log(`[phoebe-chat] ${path}: malformed response body — failing closed`);
      return false;
    }
    if (debug) console.log(`[phoebe-chat] ${path}: resolved enabled=${enabled}`);
    return enabled;
  } catch (err) {
    if (debug) {
      const reason = controller.signal.aborted ? "timeout" : "network error";
      console.log(`[phoebe-chat] ${path}: fetch failed (${reason}) — failing closed`, err);
    }
    return false;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function resolveLauncherEnabled(
  backendUrl: string | null | undefined,
  options: { timeoutMs?: number; debug?: boolean } = {},
): Promise<boolean> {
  return resolveEnabledFlag(backendUrl, { ...options, path: LAUNCHER_FLAG_PATH });
}

export function resolveSidePanelEnabled(
  backendUrl: string | null | undefined,
  options: { timeoutMs?: number; debug?: boolean } = {},
): Promise<boolean> {
  return resolveEnabledFlag(backendUrl, { ...options, path: SIDE_PANEL_FLAG_PATH });
}

/** Resolves whether the deployed IIFE may expose the live-chat entry point. */
export function resolveLiveChatEnabled(
  backendUrl: string | null | undefined,
  options: { timeoutMs?: number; debug?: boolean } = {},
): Promise<boolean> {
  return resolveEnabledFlag(backendUrl, { ...options, path: LIVE_CHAT_FLAG_PATH });
}
